/**
 * ExerciseForm — formulaire partage create/edit d'un exercice.
 *
 * Section "Obligatoires" toujours visible (nom, procédé, schéma, déroulement,
 * consignes), section "Détails avancés" pliable (catégorie, durée, effectif,
 * surface, description, objectifs offensifs/défensifs, organisation,
 * variantes, charge_params, pitch_orientation).
 *
 * Validation client minimale + mapping erreurs serveur via prop fieldErrors.
 * Boutons sticky : Enregistrer / Annuler.
 */
import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronUp, Plus, Save, Trash2, X } from 'lucide-react'
import { T } from '../theme'
import PitchEditor from './PitchEditor'

const PROCEDES = [
  { value: 'echauffement', label: 'Échauffement' },
  { value: 'jeu',          label: 'Jeu' },
  { value: 'situation',    label: 'Situation' },
  { value: 'exercice',     label: 'Exercice' },
  { value: 'match',        label: 'Match' },
]

const PITCH_ORIENTATIONS = [
  { value: 'landscape', label: 'Paysage' },
  { value: 'portrait',  label: 'Portrait' },
]

const emptySchema = () => ({ tokens: [], arrows: [], cones: [] })

function makeInitial(initial) {
  const e = initial || {}
  return {
    name: e.name || '',
    procede: e.procede || 'jeu',
    schema: e.schema && typeof e.schema === 'object' ? e.schema : emptySchema(),
    deroulement: e.deroulement || '',
    consignes: e.consignes || '',

    category_tactique: e.category_tactique || '',
    default_duration_minutes: e.default_duration_minutes || '',
    default_effectif: e.default_effectif || '',
    default_surface: e.default_surface || '',
    description: e.description || '',
    objectives_offensive: e.objectives?.offensive || [],
    objectives_defensive: e.objectives?.defensive || [],
    organization: e.organization || '',
    variantes: Array.isArray(e.variantes) ? e.variantes : [],
    charge_params: e.charge_params && typeof e.charge_params === 'object' ? e.charge_params : {},
    pitch_orientation: e.pitch_orientation || 'landscape',
  }
}

export default function ExerciseForm({
  initial = null,
  onSubmit,
  onCancel,
  isLoading = false,
  errors = {},      // erreurs serveur par champ : { name: 'msg', ... }
  serverError = null,
  submitLabel = null,
}) {
  const [form, setForm] = useState(() => makeInitial(initial))
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [localErrors, setLocalErrors] = useState({})

  // Reset si initial change (cas edit -> recharge)
  useEffect(() => {
    setForm(makeInitial(initial))
    setLocalErrors({})
  }, [initial])

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const allErrors = useMemo(() => ({ ...localErrors, ...errors }), [localErrors, errors])

  const validate = () => {
    const errs = {}
    if (!form.name.trim()) errs.name = 'Nom obligatoire.'
    else if (form.name.length > 200) errs.name = 'Maximum 200 caractères.'
    if (!form.procede) errs.procede = 'Procédé obligatoire.'
    if (!form.deroulement.trim()) errs.deroulement = 'Déroulement obligatoire.'
    else if (form.deroulement.length > 2000) errs.deroulement = 'Maximum 2000 caractères.'
    if (!form.consignes.trim()) errs.consignes = 'Consignes obligatoires.'
    else if (form.consignes.length > 1000) errs.consignes = 'Maximum 1000 caractères.'

    const tokens = form.schema?.tokens || []
    if (tokens.filter(t => t.type !== 'ball').length === 0) {
      errs.schema = 'Place au moins un pion sur le terrain.'
    }
    setLocalErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = (e) => {
    e?.preventDefault?.()
    if (!validate()) return
    // Construction du payload backend
    const payload = {
      name: form.name.trim(),
      procede: form.procede,
      schema: form.schema,
      deroulement: form.deroulement.trim() || null,
      consignes: form.consignes.trim() || null,
      pitch_orientation: form.pitch_orientation,
    }
    // Champs avancés : on n'envoie que les non-vides
    if (form.category_tactique.trim()) payload.category_tactique = form.category_tactique.trim()
    if (form.default_duration_minutes) {
      const n = parseInt(form.default_duration_minutes, 10)
      if (!isNaN(n)) payload.default_duration_minutes = n
    }
    if (form.default_effectif.trim()) payload.default_effectif = form.default_effectif.trim()
    if (form.default_surface.trim()) payload.default_surface = form.default_surface.trim()
    if (form.description.trim()) payload.description = form.description.trim()
    if (form.organization.trim()) payload.organization = form.organization.trim()
    if (form.objectives_offensive.length || form.objectives_defensive.length) {
      payload.objectives = {
        offensive: form.objectives_offensive.filter(s => s.trim()),
        defensive: form.objectives_defensive.filter(s => s.trim()),
      }
    }
    if (form.variantes.length) {
      payload.variantes = form.variantes.filter(s => s.trim())
    }
    if (Object.keys(form.charge_params).length) {
      payload.charge_params = form.charge_params
    }
    onSubmit?.(payload)
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Bandeau erreur serveur global */}
      {serverError && (
        <div style={{
          padding: '12px 14px', background: T.redBg, border: `2px solid ${T.red}`,
          fontFamily: T.mono, fontSize: 12, color: T.ink,
        }}>
          {serverError}
        </div>
      )}

      {/* Bouton sticky en haut */}
      <StickyBar isLoading={isLoading} onCancel={onCancel} submitLabel={submitLabel} />

      {/* SECTION OBLIGATOIRES */}
      <Section title="Informations principales">
        <Field label="Nom de l'exercice" error={allErrors.name} required>
          <input
            type="text"
            value={form.name}
            maxLength={200}
            onChange={(e) => set('name', e.target.value)}
            style={inputStyle}
            placeholder="Ex : Rondo 4v2 classique"
          />
        </Field>

        <Field label="Procédé" error={allErrors.procede} required>
          <select
            value={form.procede}
            onChange={(e) => set('procede', e.target.value)}
            style={inputStyle}
          >
            {PROCEDES.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </Field>

        <Field label="Schéma tactique (terrain)" error={allErrors.schema} required>
          <PitchEditor
            // key stable pendant l'edition d'un meme exo, change quand on
            // bascule sur un autre exo (Edit -> autre Edit) pour forcer
            // un remount propre avec la nouvelle initialSchema. Sans cette
            // key, le composant resterait monte avec son etat initial
            // (uncontrolled apres mount) et n'afficherait pas la nouvelle
            // donnee.
            key={initial?.id || 'new'}
            initialSchema={form.schema}
            initialOrientation={form.pitch_orientation}
            onChange={(schema, orientation) => {
              set('schema', schema)
              set('pitch_orientation', orientation)
            }}
            showToolbar
          />
        </Field>

        <Field label="Déroulement" error={allErrors.deroulement} required hint={`${form.deroulement.length}/2000`}>
          <textarea
            value={form.deroulement}
            maxLength={2000}
            rows={6}
            onChange={(e) => set('deroulement', e.target.value)}
            style={textareaStyle}
            placeholder="Décris le déroulement précis de l'exercice..."
          />
        </Field>

        <Field label="Consignes" error={allErrors.consignes} required hint={`${form.consignes.length}/1000`}>
          <textarea
            value={form.consignes}
            maxLength={1000}
            rows={4}
            onChange={(e) => set('consignes', e.target.value)}
            style={textareaStyle}
            placeholder="Liste tes consignes (une par ligne)..."
          />
        </Field>
      </Section>

      {/* SECTION DÉTAILS AVANCÉS (pliable) */}
      <button
        type="button"
        onClick={() => setAdvancedOpen(v => !v)}
        style={{
          background: T.surface, border: `2px solid ${T.dark}`,
          padding: '12px 16px', cursor: 'pointer',
          fontFamily: T.mono, fontSize: 11, fontWeight: 700,
          letterSpacing: '.14em', textTransform: 'uppercase',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        Détails avancés
        {advancedOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {advancedOpen && (
        <Section title="Détails avancés">
          <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            <Field label="Catégorie tactique">
              <input
                type="text"
                value={form.category_tactique}
                onChange={(e) => set('category_tactique', e.target.value)}
                style={inputStyle}
                placeholder="Ex : Conservation"
              />
            </Field>
            <Field label="Durée (min)" error={allErrors.default_duration_minutes}>
              <input
                type="number"
                min={1}
                max={480}
                value={form.default_duration_minutes}
                onChange={(e) => set('default_duration_minutes', e.target.value)}
                style={inputStyle}
                placeholder="15"
              />
            </Field>
            <Field label="Effectif">
              <input
                type="text"
                value={form.default_effectif}
                onChange={(e) => set('default_effectif', e.target.value)}
                style={inputStyle}
                placeholder="6v6+2GB"
              />
            </Field>
            <Field label="Surface">
              <input
                type="text"
                value={form.default_surface}
                onChange={(e) => set('default_surface', e.target.value)}
                style={inputStyle}
                placeholder="30×40 m"
              />
            </Field>
          </div>

          <Field label="Description courte (1 ligne, pour le catalogue)">
            <input
              type="text"
              value={form.description}
              maxLength={300}
              onChange={(e) => set('description', e.target.value)}
              style={inputStyle}
              placeholder="Phrase descriptive courte affichée sur la carte du catalogue."
            />
          </Field>

          <Field label="Organisation (mise en place)">
            <textarea
              value={form.organization}
              rows={3}
              onChange={(e) => set('organization', e.target.value)}
              style={textareaStyle}
              placeholder="Disposition du terrain, plots, équipes..."
            />
          </Field>

          <ListEditor
            label="Objectifs offensifs"
            items={form.objectives_offensive}
            onChange={(arr) => set('objectives_offensive', arr)}
            placeholder="Ex : Vitesse de transmission"
          />

          <ListEditor
            label="Objectifs défensifs"
            items={form.objectives_defensive}
            onChange={(arr) => set('objectives_defensive', arr)}
            placeholder="Ex : Pressing collectif"
          />

          <ListEditor
            label="Variantes"
            items={form.variantes}
            onChange={(arr) => set('variantes', arr)}
            placeholder="Ex : 1 touche imposée"
          />

          <Field label="Charge / paramètres physiques">
            <ChargeParamsForm
              value={form.charge_params}
              onChange={(v) => set('charge_params', v)}
            />
          </Field>

          <Field label="Orientation par défaut du terrain">
            <div style={{ display: 'flex', gap: 12 }}>
              {PITCH_ORIENTATIONS.map(o => (
                <label key={o.value} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  fontFamily: T.mono, fontSize: 11, color: T.ink,
                  cursor: 'pointer',
                }}>
                  <input
                    type="radio"
                    name="pitch_orientation"
                    value={o.value}
                    checked={form.pitch_orientation === o.value}
                    onChange={(e) => set('pitch_orientation', e.target.value)}
                    style={{ accentColor: T.gold }}
                  />
                  {o.label}
                </label>
              ))}
            </div>
          </Field>
        </Section>
      )}

      {/* Bouton bas */}
      <StickyBar isLoading={isLoading} onCancel={onCancel} submitLabel={submitLabel} bottom />
    </form>
  )
}


// ─────────────────────────────────────────────────────────────────────
// Sous-composants
// ─────────────────────────────────────────────────────────────────────

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  background: T.surface,
  border: `2px solid ${T.dark}`,
  fontFamily: T.mono, fontSize: 12, color: T.ink,
  outline: 'none',
}
const textareaStyle = { ...inputStyle, resize: 'vertical', fontFamily: T.mono }

function Section({ title, children }) {
  return (
    <div style={{
      background: T.surface, border: `2px solid ${T.dark}`,
      boxShadow: `4px 4px 0 ${T.dark}`,
      padding: '20px 22px',
    }}>
      {title && (
        <div style={{
          fontFamily: T.display, fontSize: 18, color: T.ink,
          textTransform: 'uppercase', letterSpacing: '.02em',
          marginBottom: 14,
          paddingBottom: 8, borderBottom: `2px solid ${T.dark}`,
        }}>
          {title}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {children}
      </div>
    </div>
  )
}

function Field({ label, required, error, hint, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <label style={{
          fontFamily: T.mono, fontSize: 10, letterSpacing: '.14em',
          textTransform: 'uppercase', color: T.ink, fontWeight: 700,
        }}>
          {label}{required && <span style={{ color: T.red, marginLeft: 4 }}>*</span>}
        </label>
        {hint && (
          <span style={{ fontFamily: T.mono, fontSize: 9, color: T.muted, letterSpacing: '.04em' }}>
            {hint}
          </span>
        )}
      </div>
      {children}
      {error && (
        <div style={{
          fontFamily: T.mono, fontSize: 10, color: T.red,
          letterSpacing: '.04em',
        }}>
          {error}
        </div>
      )}
    </div>
  )
}

function ListEditor({ label, items, onChange, placeholder }) {
  const [draft, setDraft] = useState('')
  const add = () => {
    const v = draft.trim()
    if (!v) return
    onChange([...items, v])
    setDraft('')
  }
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i))
  return (
    <Field label={label}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((it, i) => (
          <div key={i} style={{
            display: 'flex', gap: 8, alignItems: 'center',
            padding: '6px 10px', background: T.bgAlt, border: `1.5px solid ${T.rule}`,
          }}>
            <input
              type="text"
              value={it}
              onChange={(e) => {
                const arr = [...items]; arr[i] = e.target.value; onChange(arr)
              }}
              style={{
                flex: 1, border: 'none', outline: 'none',
                background: 'transparent',
                fontFamily: T.mono, fontSize: 11, color: T.ink,
              }}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              aria-label="Supprimer"
              style={{
                background: 'transparent', border: `1.5px solid ${T.red}`,
                padding: 4, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center',
              }}
            >
              <X size={11} color={T.red} />
            </button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            type="text"
            value={draft}
            placeholder={placeholder}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            type="button"
            onClick={add}
            style={{
              background: T.gold, color: T.dark, border: `2px solid ${T.dark}`,
              padding: '6px 12px', cursor: 'pointer',
              fontFamily: T.mono, fontSize: 10, fontWeight: 700,
              letterSpacing: '.12em', textTransform: 'uppercase',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            <Plus size={11} /> Ajouter
          </button>
        </div>
      </div>
    </Field>
  )
}

function ChargeParamsForm({ value, onChange }) {
  const set = (k, v) => {
    const next = { ...value }
    if (v === '' || v === null || v === undefined) delete next[k]
    else next[k] = v
    onChange(next)
  }
  return (
    <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
      <SubField label="Intensité">
        <select
          value={value.intensite || ''}
          onChange={(e) => set('intensite', e.target.value)}
          style={inputStyle}
        >
          <option value="">—</option>
          <option value="faible">Faible</option>
          <option value="moyen">Moyen</option>
          <option value="moyen-fort">Moyen-fort</option>
          <option value="fort">Fort</option>
          <option value="tres-fort">Très fort</option>
        </select>
      </SubField>
      <SubField label="Temps de travail">
        <input
          type="text"
          value={value.temps_travail || ''}
          onChange={(e) => set('temps_travail', e.target.value)}
          style={inputStyle}
          placeholder="3 min"
        />
      </SubField>
      <SubField label="Récupération">
        <input
          type="text"
          value={value.temps_recup || ''}
          onChange={(e) => set('temps_recup', e.target.value)}
          style={inputStyle}
          placeholder="30 s"
        />
      </SubField>
      <SubField label="Répétitions">
        <input
          type="number"
          min={1}
          max={20}
          value={value.repetitions || ''}
          onChange={(e) => set('repetitions', parseInt(e.target.value, 10) || '')}
          style={inputStyle}
          placeholder="4"
        />
      </SubField>
    </div>
  )
}

function SubField({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{
        fontFamily: T.mono, fontSize: 9, letterSpacing: '.12em',
        textTransform: 'uppercase', color: T.muted2,
      }}>
        {label}
      </span>
      {children}
    </label>
  )
}

function StickyBar({ isLoading, onCancel, submitLabel, bottom = false }) {
  return (
    <div style={{
      display: 'flex', gap: 8, justifyContent: 'flex-end',
      ...(bottom ? {} : {
        position: 'sticky', top: 0, zIndex: 10,
        background: T.bg, padding: '8px 0', marginBottom: -8,
      }),
    }}>
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          style={{
            background: T.surface, color: T.ink, border: `2px solid ${T.dark}`,
            padding: '10px 16px', cursor: isLoading ? 'not-allowed' : 'pointer',
            fontFamily: T.mono, fontSize: 11, fontWeight: 700,
            letterSpacing: '.12em', textTransform: 'uppercase',
            opacity: isLoading ? 0.5 : 1,
          }}
        >
          Annuler
        </button>
      )}
      <button
        type="submit"
        disabled={isLoading}
        style={{
          background: T.gold, color: T.dark, border: `2px solid ${T.dark}`,
          boxShadow: `4px 4px 0 ${T.dark}`,
          padding: '10px 18px', cursor: isLoading ? 'wait' : 'pointer',
          fontFamily: T.mono, fontSize: 11, fontWeight: 700,
          letterSpacing: '.14em', textTransform: 'uppercase',
          display: 'inline-flex', alignItems: 'center', gap: 8,
          opacity: isLoading ? 0.7 : 1,
        }}
      >
        <Save size={12} /> {submitLabel || (isLoading ? 'Enregistrement...' : 'Enregistrer')}
      </button>
    </div>
  )
}
